import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Sidebar from './components/navigation/Sidebar';
import Dashboard from './pages/Dashboard';
import Athletes from './pages/Athletes';
import Coaches from './pages/Coaches';
import SiteManagers from './pages/SiteManagers';
import Session from './pages/Session';
import Financials from './pages/Financials';
import Locations from './pages/Locations';
import Sessions from './pages/Sessions';
import ContentFeed from './pages/ContentFeed';
import Employees from './pages/Employees';
import Athlete from './pages/Athlete';
import Coach from './pages/Coach';
import SiteManager from './pages/SiteManager';
import Employee from './pages/Employee';
import BookingDashboard from './pages/BookingDashboard';
import Waitlist from './pages/Waitlist';
import WaitlistMember from './pages/Waitlist-member';
import Markets from './pages/Markets';
import CountryOverview from './pages/CountryOverview';
import StateOverview from './pages/StateOverview';
import CityOverview from './pages/CityOverview';
import Leads from './pages/Leads';
import Lead from './pages/Lead';
import Login from './pages/Login';
import Applicants from './pages/Applicants';
import ApplicantOverview from './pages/ApplicantOverview';
import FacilitiesCity from './pages/FacilitiesCity';
import FacilityChangelog from './pages/FacilityChangelog';
import FacilitiesRegions from './pages/FacilitiesRegions';
import FacilitiesCountry from './pages/FacilitiesCountry';
import Facilities from './pages/Facilities';
import FacilityDetails from './pages/FacilityDetails';
import Marketing from './pages/Marketing';
import Partner from './pages/Partner';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import PartnersPage from './pages/PartnersPage';
import Outreach from './pages/Outreach';
import ScanHistory from './pages/ScanHistory';
import WhookStream from './pages/WhookStream';
import CreateDemoAccount from './pages/CreateDemoAccount';
import RoleRedirect from './components/RoleRedirect';
import PreferencesPage from './pages/PreferencesPage';
import Upload from './pages/Upload';
import Enterprises from './pages/Enterprises';
import EnterprisePage from './pages/Enterprise';
import Couriers from './pages/Couriers';
import Courier from './pages/Courier';
import ExperiencePricing from './pages/ExperiencePricing';
import GenesisPage from './pages/enterprise-ai/GenesisPage';
import AgentBuilderPage from './pages/enterprise-ai/AgentBuilderPage';
import TranscriptsPage from './pages/enterprise-ai/TranscriptsPage';
import MemoryPage from './pages/enterprise-ai/MemoryPage';
import EvalHarnessPage from './pages/enterprise-ai/EvalHarnessPage';
import OutboundPage from './pages/enterprise-ai/OutboundPage';
import LeadsPage from './pages/enterprise-ai/LeadsPage';
import MoneyPage from './pages/enterprise-ai/MoneyPage';
import PricingPage from './pages/enterprise-ai/PricingPage';


const App = () => {
    const [open, setOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();
        // Listen for authentication state changes
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
                // Redirect to login if not authenticated and not already on the login page
                if (location.pathname !== '/login') {
                    navigate('/login');
                }
            }
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [navigate, location.pathname]);

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleLogout = () => {
        getAuth().signOut();  // Log the user out
        navigate('/login'); // Redirect to login page
    };

    return (
        <>
            {location.pathname !== '/login' && isAuthenticated && (
                <Sidebar open={open} handleDrawerToggle={handleDrawerToggle} handleLogout={handleLogout} />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    height: '100vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    backgroundColor: '#1c1c1c',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    // No marginLeft here: #root is display:flex (index.css:19),
                    // and the permanent Drawer's .MuiDrawer-docked root already
                    // occupies its width (240/60) in flex layout. Adding
                    // marginLeft on top double-counted the sidebar and produced
                    // a 480px/120px leading gap.
                    // Scrollbar styles
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#1c1c1c',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#12c099',
                        borderRadius: '10px',
                    },
                }}
            >
                <Routes>
                    <Route path="/login" element={<Login />} />

                    {isAuthenticated && (
                        <>
                            {/* NEW — UNITED FOR BUSINESS / ENTERPRISE */}
                            <Route path="/enterprise/:id" element={<EnterprisePage />} />

                            {/* ENTERPRISE AI */}
                            <Route path="/genesis" element={<GenesisPage />} />
                            <Route path="/agent-builder" element={<AgentBuilderPage />} />
                            <Route path="/transcripts" element={<TranscriptsPage />} />
                            <Route path="/memory" element={<MemoryPage />} />
                            <Route path="/eval" element={<EvalHarnessPage />} />
                            <Route path="/outbound" element={<OutboundPage />} />
                            <Route path="/inbound-leads" element={<LeadsPage />} />
                            <Route path="/money" element={<MoneyPage />} />
                            <Route path="/united-pricing" element={<PricingPage />} />

                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/athletes" element={<Athletes />} />
                            <Route path="/whook" element={<WhookStream />} />
                            <Route path="/athlete/:firebaseUid" element={<Athlete />} />
                            <Route path="/scanhistory/:partnerId" element={<ScanHistory />} />
                            <Route path="/coaches" element={<Coaches />} />
                            <Route path="/coach/:firebaseUid" element={<Coach />} />
                            <Route path="/facilities" element={<Facilities />} />
                            <Route path="/sitemanagers" element={<SiteManagers />} />
                            <Route path="/preferences" element={<PreferencesPage />} />
                            <Route path="/site-manager/:firebaseUid" element={<SiteManager />} />
                            <Route path="/session/:id" element={<Session />} />
                            <Route path="/financials" element={<Financials />} />
                            <Route path="/countryoverview/:country" element={<CountryOverview />} />
                            <Route path="/facilities/:id" element={<FacilityDetails />} />
                            <Route path="/stateoverview/:state" element={<StateOverview />} />
                            <Route path="/facilities/:country/:region/city/" element={<FacilitiesCity />} />
                            <Route path="/facilities/:id/changelog" element={<FacilityChangelog />} />
                            <Route path="/facilities/:country/regions/" element={<FacilitiesRegions />} />
                            <Route path="/facilities/countries" element={<FacilitiesCountry />} />
                            <Route path="/facilities/:country/:region/:city" element={<Facilities />} />
                            <Route path="/cityoverview/:city" element={<CityOverview />} />
                            <Route path="/sessions" element={<Sessions />} />
                            <Route path="/contentfeed" element={<ContentFeed />} />
                            <Route path="/partners" element={<PartnersPage />} />
                            <Route path="/employees" element={<Employees />} />
                            <Route path="/partner/:id" element={<Partner />} />
                            <Route path="/employee/:firebaseUid" element={<Employee />} />
                            <Route path="/bookingdashboard" element={<BookingDashboard />} />
                            <Route path="/leads" element={<Leads />} />
                            <Route path="/leads/:id" element={<Lead />} />
                            <Route path="/waitlist" element={<Waitlist />} />
                            <Route path="/waitlist-member/:id" element={<WaitlistMember />} />
                            <Route path="/markets" element={<Markets />} />
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/applicants" element={<Applicants />} />
                            <Route path="/marketing" element={<Marketing />} />
                            <Route path="/outreach" element={<Outreach />} />
                            <Route path="/applicants/:id" element={<ApplicantOverview />} />
                            <Route path="/demo" element={<CreateDemoAccount />} />
                            <Route path="/upload" element={<Upload />} />
                            <Route path="/couriers" element={<Couriers />} />
                            <Route path="/courier/:firebaseUid" element={<Courier />} />
                            <Route path="/enterprises" element={<Enterprises />} />
                            <Route path="/pricing" element={<ExperiencePricing />} />


                        </>
                    )}
                </Routes>

            </Box>
        </>
    );
}

export default App;
